import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SETUP_SECTIONS } from '../../domain/setup/sections';
import { compactMoney, date, money } from '../../lib/format';
import { BASIS_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import {
  AboutThisPage,
  EmptyState,
  PageHeader,
  SectionCard,
  StatusChip,
} from '../../ui/primitives';

export default function SetupHomePage() {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const navigate = useNavigate();
  const startedIds = new Set(scope.fundSetups.map((setup) => setup.opportunityId));
  const start = (opportunityId: string) => {
    const setupId = `su-${Date.now().toString(36)}`;
    dispatch({ type: 'SETUP_START', opportunityId, setupId });
    navigate(`/manager/setup/${setupId}`);
  };
  return (
    <>
      <PageHeader
        title="Set up a new deal"
        subtitle={`Signed in as ${scope.user.name}. Pick an approved opportunity: the trust, unit class and offer are pre-filled from it, from Fides defaults and from your profile.`}
      />
      <AboutThisPage title="About this flow">
        <p>
          The steps follow the order Fides sets up a deal: trust, contacts, branding, bank, investor
          rules, unit class, activation, then the offer.
        </p>
        <p>
          Anything Fides does the same way every time is a default, set in Settings, that you can
          change per fund. Details only you can supply, such as the trust ABN and bank account,
          start blank. The deals are fictional.
        </p>
      </AboutThisPage>
      {scope.fundSetups.length > 0 && (
        <SectionCard title="In progress" sx={{ mb: 3 }}>
          <Stack spacing={1.5}>
            {scope.fundSetups.map((setup) => {
              const done = SETUP_SECTIONS.filter((section) =>
                setup.completed.includes(section.id),
              ).length;
              return (
                <Paper key={setup.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ sm: 'center' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: 700 }}>
                          {String(setup.values['fund.name'])}
                        </Typography>
                        <StatusChip status={setup.status} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {done} of {SETUP_SECTIONS.length} steps · updated {date(setup.updatedAt)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(done / SETUP_SECTIONS.length) * 100}
                        sx={{ mt: 1, height: 4, borderRadius: 2, maxWidth: 360 }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/manager/setup/${setup.id}`)}
                    >
                      {setup.status === 'PUBLISHED' ? 'View' : 'Continue'}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </SectionCard>
      )}
      <SectionCard
        title="Approved opportunities"
        subtitle="From the deal pipeline, ready for a trust"
      >
        {scope.opportunities.length === 0 ? (
          <EmptyState title="No opportunities" />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            {scope.opportunities.map((opportunity) => (
              <Paper
                key={opportunity.id}
                variant="outlined"
                sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
              >
                <Typography variant="caption" color="text.secondary">
                  {opportunity.strategy} · {opportunity.location}
                </Typography>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>{opportunity.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
                  {opportunity.summary}
                </Typography>
                <Typography variant="caption" component="div">
                  Target {compactMoney(opportunity.targetSize)} · {opportunity.targetReturnPct}%
                  p.a. · min {money(opportunity.minInvestment)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ mb: 1.5 }}
                >
                  {BASIS_LABELS[opportunity.basis]}
                  {opportunity.partlyPaid ? ' · partly paid' : ''} · book build closes{' '}
                  {date(opportunity.eoiCloseDate)}
                </Typography>
                <Button
                  variant={startedIds.has(opportunity.id) ? 'outlined' : 'contained'}
                  onClick={() => start(opportunity.id)}
                >
                  {startedIds.has(opportunity.id) ? 'Start another setup' : 'Start setup'}
                </Button>
              </Paper>
            ))}
          </Box>
        )}
      </SectionCard>
    </>
  );
}
